import React from "react";
import { Badge, Box, Card, Flex, List, Text } from "@chakra-ui/react";
import { LuCheck, LuX } from "react-icons/lu";
import type { PlanFeature, PlanTier } from "./types";

interface PlanCardProps {
  plan: PlanTier;
  planView: "annual" | "monthly";
  isCurrentPlan: boolean;
  button: React.ReactNode | null;
}

export const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  planView,
  isCurrentPlan,
  button,
}) => {
  const price = planView === "annual" ? plan.annualPrice : plan.monthlyPrice;
  const titleText = isCurrentPlan ? `${plan.title} (Current Plan)` : plan.title;
  const isRecommended = !!plan.recommended;

  return (
    <Card.Root
      flex={1}
      minWidth="240px"
      width="full"
      alignSelf="stretch"
      css={isRecommended && !isCurrentPlan
        ? { border: "2px solid", borderColor: "#2dd4bf", boxShadow: "0px 0px 1px 0px rgba(24,24,27,0.3), 0px 4px 8px 0px rgba(24,24,27,0.1)" }
        : isCurrentPlan
        ? { bg: "#f4f4f5" }
        : { boxShadow: "0px 0px 1px 0px rgba(24,24,27,0.3), 0px 2px 4px 0px rgba(24,24,27,0.1)" }}
    >
      <Card.Body display="flex" flexDir="column">
        <Flex alignItems="center" gap="2px" mb={0}>
          <Text
            fontSize="md"
            fontWeight="bold"
            lineHeight="24px"
            color={isRecommended && !isCurrentPlan ? "#2dd4bf" : "black"}
            flex={isRecommended && !isCurrentPlan ? 1 : undefined}
          >
            {titleText}
          </Text>
          {isRecommended && !isCurrentPlan && (
            <Badge colorPalette="teal" variant="subtle">Recommended</Badge>
          )}
        </Flex>

        <Flex flexDir="column" gap={0} mb={6}>
          <Flex alignItems="center" gap={2}>
            <Text fontSize="48px" fontWeight={800} lineHeight="60px">${price}</Text>
            <Box>
              <Text fontSize="sm" color="#a1a1aa" lineHeight="20px">
                USD
              </Text>
              <Text fontSize="sm" color="#a1a1aa" lineHeight="20px">/month</Text>
            </Box>
          </Flex>
          {planView === "annual" && (
            <Text fontSize="sm" color="#a1a1aa" lineHeight="20px">
              Billed as: ${plan.annualBilled?.toLocaleString()} per year
            </Text>
          )}
        </Flex>

        <Text fontSize="sm" color="#52525b" lineHeight="20px" mb={6}>
          {plan.description}
        </Text>

        <FeatureList features={plan.features} />

        {button && <Box pt={2} width="full">{button}</Box>}
      </Card.Body>
    </Card.Root>
  );
};

// ── Shared feature list ───────────────────────────────────────────────────────

interface FeatureListProps {
  features: PlanFeature[];
}

export const FeatureList: React.FC<FeatureListProps> = ({ features }) => (
  <List.Root as="ol" variant="plain" gap="6px" flex={1}>
    {features.map((feature, i) => (
      <List.Item
        key={i}
        fontSize="sm"
        color={feature.included ? "#52525b" : "#a1a1aa"}
        display="flex"
        alignItems="flex-start"
        gap="6px"
        lineHeight="20px"
      >
        {feature.included ? (
          <LuCheck style={{ flexShrink: 0, width: 24, height: 24 }} color="#2dd4bf" />
        ) : (
          <LuX style={{ flexShrink: 0, width: 24, height: 24 }} color="#d4d4d8" />
        )}
        {feature.text}
      </List.Item>
    ))}
  </List.Root>
);
